/** Google Ads browser tag and conversion dispatch. */
import { state, debugLog } from "./state.js";
import { isMarketingAllowed } from "./consent.js";
import { DEFAULT_CURRENCY } from "./constants.js";
import { recordPixelPayload, type EventPayload } from "./types.js";
import { googleTagUserData } from "./google-user-data.js";

// --- Google Ads ---

/** Load Google Ads gtag.js. */
export function loadGoogleAdsPixel(conversionId: string): void {
  if (state.initializedPixels["ga_" + conversionId]) return;
  state.initializedPixels["ga_" + conversionId] = true;

  if (window.gtag) {
    state.selfDispatch = true;
    window.gtag("config", conversionId);
    state.selfDispatch = false;
    return;
  }

  const scriptElement = document.createElement("script");
  scriptElement.async = true;
  scriptElement.src = "https://www.googletagmanager.com/gtag/js?id=" + conversionId;
  document.head.appendChild(scriptElement);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer!.push(arguments);
  };
  state.selfDispatch = true;
  window.gtag("js", new Date());
  state.selfDispatch = false;
  state.selfDispatch = true;
  window.gtag("config", conversionId);
  state.selfDispatch = false;
}

/** Fire Google Ads gtag conversion event. */
export function fireGoogleAdsPixel(payload: EventPayload, eventId: string): void {
  if (!isMarketingAllowed()) return;
  if (!window.gtag) return;
  if (!state.config || !state.config.platforms || !state.config.platforms.google_ads) return;

  const previousSelfDispatch = state.selfDispatch;
  try {
    const gaConfig = state.config.platforms.google_ads;
    // Without a conversion id there is no Google Ads tag to address; firing
    // without send_to would broadcast the event to every gtag on the page
    // (polluting the site's own GA4 property with "conversion"/lowercase events)
    if (!gaConfig.conversionId) return;
    // PageView is covered by gtag's automatic page ping on config, the ping
    // that fills remarketing lists. Same convention as GA4 and TikTok: report
    // it as fired when the pixel was initialized, because gtag.js loads async
    // and the dataLayer queue replays the config call on load.
    if (payload.eventName === "PageView") {
      if (state.initializedPixels["ga_" + gaConfig.conversionId]) {
        payload.pixelsFired.push("google_ads");
        recordPixelPayload(payload, "google_ads", { method: "gtag", args: ["config", gaConfig.conversionId], note: "remarketing page ping fires automatically on config" });
      }
      return;
    }
    // Without a per-action label a gtag conversion event has no conversion
    // action to land in: Google counts conversions per action ("AW-id/label").
    // The per-event label (multi-source WEBPAGE action, mirrors the server
    // upload's target) wins over the destination-level fallback. No label at
    // all = the server channel is the only counter and the tag's job is
    // remarketing, which loadGoogleAdsPixel's config call already covers;
    // event pings would be noise.
    const eventLabels = gaConfig.eventLabels || {};
    const label = eventLabels[payload.eventName!] || gaConfig.conversionLabel;
    if (!label) return;
    // Every labeled event is addressed as a conversion for its own action;
    // the action's category in Google Ads carries the semantics.
    const gaEventName = "conversion";

    const sendTo = gaConfig.conversionId + "/" + label;

    // A real order id pairs with the offline upload's transactionId so
    // Google can dedup the tag conversion against our API upload
    const gaOrderId = payload.customData?.order_id;
    const gaParams: Record<string, unknown> = {
      send_to: sendTo,
      transaction_id: typeof gaOrderId === "string" && gaOrderId ? gaOrderId : eventId,
    };
    if (payload.eventValue != null) {
      gaParams.value = payload.eventValue;
      gaParams.currency = payload.currency || DEFAULT_CURRENCY;
    }

    const userData = googleTagUserData(payload.userData ?? state.userData);
    state.selfDispatch = true;
    window.gtag("set", "user_data", userData);
    window.gtag("event", gaEventName, gaParams);
    state.selfDispatch = false;
    payload.pixelsFired.push("google_ads");
    recordPixelPayload(payload, "google_ads", {
      method: "gtag", args: ["event", gaEventName, gaParams],
      userDataFields: Object.keys(userData),
      note: "SDK call queued; user data fields: " + (Object.keys(userData).join(", ") || "none") + "; receipt and attribution are verified separately",
    });
  } catch {
    debugLog("google_ads", "conversion SDK call failed");
  } finally {
    state.selfDispatch = previousSelfDispatch;
  }
}
